import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.main}`,
  overflow: 'hidden',
  '&:first-of-type': {
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10
  },
  '&:last-of-type': {
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10
  },
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&::before': {
    display: 'none'
  }
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon />} {...props} />
))(({ theme }) => ({
  backgroundColor: theme.palette.backgroundLight.main,
  padding: '0px 44px',
  [theme.breakpoints.down('md')]: {
    backgroundColor: theme.palette.grey[600],
    paddingLeft: 20,
    paddingRight: 20
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(90deg)',
    '& svg': {
      color: theme.palette.black.main,
      [theme.breakpoints.down('md')]: {
        color: theme.palette.white.main
      }
    }
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(270deg)'
  },
  '& .MuiAccordionSummary-content': {
    margin: '25px 0'
  },
  '& .MuiTypography-root': {
    fontSize: 21,
    [theme.breakpoints.down('md')]: {
      fontSize: 16,
      color: theme.palette.white.main
    }
  }
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  backgroundColor: theme.palette.backgroundLight.main,
  paddingLeft: 44,
  paddingRight: 44,
  paddingTop: 0,
  [theme.breakpoints.down('md')]: {
    backgroundColor: theme.palette.grey[600],
    paddingLeft: 0,
    paddingRight: 0
  },
  '& .MuiTypography-root': {
    fontSize: 16,
    maxWidth: 1000,
    [theme.breakpoints.down('md')]: {
      fontSize: 14,
      color: theme.palette.white.main,
      maxWidth: 'unset',
      paddingLeft: 20,
      paddingRight: 20
    }
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 24,
  fontSize: 32,
  [theme.breakpoints.down('md')]: {
    marginBottom: 30,
    fontSize: 28,
    color: theme.palette.white.main
  }
}));

const Wrapper = styled('div')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  maxWidth: 1240,
  zIndex: 100,
  position: 'relative',
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30,
    flexDirection: 'column'
  },
  [theme.breakpoints.down('md')]: {
    padding: '30px 20px',
    paddingBottom: 50,
    backgroundColor: theme.palette.grey[600]
  }
}));

const Questions = () => {
  const t = useTranslate('Consultant');
  const tFaq = useTranslate('FAQ');
  const [expanded, setExpanded] = React.useState(false);

  const items = React.useMemo(
    () =>
      [1, 2, 3, 4, 5, 6].map((i) => ({
        id: `faq-${i}`,
        question: tFaq(`Q${i}`),
        answer: tFaq(`A${i}`)
      })),
    [tFaq]
  );

  const handleChange = (panel) => (_, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
    import('services/analytics').then(({ default: A }) => A.event('OpenQuestion', {
      question: items.find((item) => item.id === panel)?.question
    }));
  };

  return (
    <Wrapper>
      <Title variant="h2">{t('FAQTitle')}</Title>
      {items.map((item) => (
        <Accordion key={item.id} expanded={expanded === item.id} onChange={handleChange(item.id)}>
          <AccordionSummary>
            <Typography>{item.question}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>{item.answer}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Wrapper>
  );
};

export default Questions;
